import { ReactNode, useMemo, useState } from 'react'
import { Combobox } from '@systemeio/components'
import { ChevronDownIcon, CheckIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import useUser from 'shared/hooks/use-user'
import { useCreateTopic } from '../api/use-create-topic'
import { generatePathFromName } from '../hooks/path-utils'
import { useCommunityWithTopics } from '../hooks/use-community-with-topics'

interface TopicSelectProps {
  error?: string
  value?: number
  onChange: (topicId: number | undefined) => void
}

const CREATE_DATA_OPTION_ID = -1

interface TopicOption {
  id: number | null
  caption: string
}

export default function TopicSelect({ error, value, onChange }: TopicSelectProps): ReactNode {
  const { t } = useTranslation()

  const [createError, setCreateError] = useState<string>()
  const [isCreating, setIsCreating] = useState(false)
  const [query, setQuery] = useState('')

  const { isOwner } = useUser()
  const createTopicMutation = useCreateTopic()
  const { data: community, mutate } = useCommunityWithTopics()
  const topics = community?.topics

  const options: TopicOption[] = useMemo(
    () => (topics ? topics.map(topic => ({ id: topic.id, caption: topic.name })) : []),
    [topics],
  )

  const createNewTopic = async ({ name }: { name: string }): Promise<void> => {
    if (!community) return

    setIsCreating(true)

    try {
      const data = await createTopicMutation.trigger({
        name,
        path: generatePathFromName(name),
        community: community.id,
      })

      await mutate(
        {
          ...community,
          topics: [...community.topics, data],
        },
        false,
      )
      onChange(data.id)
    } catch (error_) {
      let errorMessage: string = ''
      if (error_ instanceof BadRequest) {
        errorMessage = [...Object.values(error_.errors?.fields ?? {}), error_.errors.common]
          .map(err => err.join('/n'))
          .join('/n')
      } else if (error_ instanceof InternalError) {
        errorMessage = t('core.error.internal_error_message')
      }

      setCreateError(errorMessage)
    }

    setIsCreating(false)
  }

  const handleChangeTopic = (data: number | string): void => {
    setCreateError('')
    onChange(Number(data))
  }

  const errorMessage = [createError, error].filter(err => !!err).join('<br />')

  const showCreateOption = isOwner && query.trim() !== ''

  const filteredItems: TopicOption[] = useMemo(() => {
    const normalizedQuery = query.toLowerCase().replaceAll(/\s+/g, '')
    const filteredOptions = normalizedQuery
      ? options.filter(option =>
          option.caption.toLowerCase().replaceAll(/\s+/g, '').includes(normalizedQuery),
        )
      : options

    return showCreateOption
      ? [{ id: CREATE_DATA_OPTION_ID, caption: query.trim() }, ...filteredOptions]
      : filteredOptions
  }, [options, query, showCreateOption])

  const selectedOption = options.find(option => option.id === value)

  return (
    <div className="flex flex-col gap-1">
      <Combobox.Root<TopicOption>
        items={filteredItems}
        filter={null}
        value={selectedOption ?? null}
        isItemEqualToValue={(item, selectedValue) => item?.id === selectedValue?.id}
        itemToStringLabel={item => item?.caption ?? ''}
        itemToStringValue={item => String(item?.id ?? '')}
        disabled={isCreating}
        onInputValueChange={(inputValue, eventDetails) => {
          setQuery(eventDetails.reason === 'input-change' ? inputValue : '')
        }}
        onValueChange={item => {
          setQuery('')
          if (!item) {
            setCreateError('')
            onChange(undefined)
            return
          }
          if (item.id === CREATE_DATA_OPTION_ID) {
            void createNewTopic({ name: item.caption })
          } else {
            handleChangeTopic(item.id as number)
          }
        }}
      >
        <Combobox.InputLabel className="w-full text-sm font-medium text-text-primary">
          {t('home.add_post.topic')}
        </Combobox.InputLabel>
        <Combobox.InputGroup
          data-invalid={!!errorMessage || undefined}
          data-disabled={isCreating || undefined}
        >
          <Combobox.Input
            data-testid="editable-form-select-input"
            placeholder={t(
              isOwner ? 'home.add_topic.choose_or_create_topic' : 'home.add_post.choose_topic',
            )}
            className={isCreating ? 'animate-pulse' : undefined}
          />
          <Combobox.Clear />
          <Combobox.Trigger data-testid="editable-form-select-trigger" aria-hidden="true">
            <Combobox.Icon>
              <ChevronDownIcon />
            </Combobox.Icon>
          </Combobox.Trigger>
        </Combobox.InputGroup>
        <Combobox.Content className="max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) overflow-y-auto">
          <Combobox.List>
            {(item: TopicOption, index: number) =>
              item.id === CREATE_DATA_OPTION_ID ? (
                <Combobox.CreateOption
                  key="create-new-topic"
                  value={item}
                  className="h-auto min-h-10 items-start py-2"
                >
                  <span className="min-w-0 flex-1 text-left break-all whitespace-normal">
                    {t('home.add_topic.create_option', {
                      name: item.caption,
                      path: generatePathFromName(item.caption),
                    })}
                  </span>
                </Combobox.CreateOption>
              ) : (
                <Combobox.Item key={item.id} value={item} index={index} data-testid="topic-option">
                  <Combobox.ItemText className="wrap-anywhere whitespace-normal">
                    {item.caption}
                  </Combobox.ItemText>
                  <Combobox.ItemIndicator>
                    <CheckIcon className="size-4" />
                  </Combobox.ItemIndicator>
                </Combobox.Item>
              )
            }
          </Combobox.List>
        </Combobox.Content>
      </Combobox.Root>
      {errorMessage && (
        <p className="text-sm text-text-error" dangerouslySetInnerHTML={{ __html: errorMessage }} />
      )}
    </div>
  )
}
